import React, { useContext, useEffect, useState } from 'react'
import classNames from 'classnames'

import { Icon } from '@scentregroup/ui'

import analyticsUtils from '../../../helpers/analytics-utils'
import trackEvent from '../../../helpers/analytics-utils/track-event'

import { Item } from '../types'
import { LinkContext } from '../context'

import style from './index.module.scss'

const attrsAllowList = ['target', 'rel', 'title']

const Pane: React.FC<{
  depth?: number
  title?: string
  items: Item[]
  initialPath?: number[]
  onClose?: () => void
  onExit?: () => void
}> = ({ depth = 0, title, items, initialPath, onClose, onExit }) => {
  const Link = useContext(LinkContext)

  const [path, setPath] = useState<number[] | undefined>(initialPath)

  useEffect(() => setPath(initialPath), [initialPath])

  return (
    <>
      <div
        className={classNames(style.pane)}
        data-test-id={`main-navdrawer-${depth}`}
      >
        {title && <div className={classNames(style.title)}>{title}</div>}
        {items.map((item, i) => {
          if (!item) {
            return null
          }

          if ('url' in item) {
            const attrs = item.attrs

            return (
              <Link
                {...(attrs &&
                  attrsAllowList.reduce(
                    (memo, key) => ({ ...memo, [key]: attrs[key] }),
                    {}
                  ))}
                key={i}
                to={item.url}
                className={classNames(item.className, style.link, style.item)}
                data-test-id={`main-navdrawer-link-${depth}-${i}`}
                onClick={() =>
                  trackEvent(
                    analyticsUtils.categories.UNIVERSAL_HEADER,
                    analyticsUtils.actions.UNIVERSAL_HEADER_NAV_LINK_CLICK,
                    `L${depth}-${i} ${item.label}`
                  )
                }
              >
                {item.label}
              </Link>
            )
          }

          if ('items' in item) {
            const children = (
              <>
                {item.title}
                <Icon
                  size="small"
                  type="chevron-right"
                  className={classNames(style.chevron, style.icon)}
                  data-test-id="main-navdrawer-expand-icon"
                />
              </>
            )

            return i === path?.[0] ? (
              <div
                key={i}
                className={classNames(item.className, style.item, style.active)}
              >
                {children}
              </div>
            ) : (
              <button
                key={i}
                className={classNames(item.className, style.item, style.link)}
                data-test-id={`main-navdrawer-button-${depth}-${i}`}
                onClick={() => {
                  setPath([i])
                  trackEvent(
                    analyticsUtils.categories.UNIVERSAL_HEADER,
                    analyticsUtils.actions.UNIVERSAL_HEADER_NAV_MENU_OPEN,
                    `L${depth}-${i} ${item.title}`
                  )
                }}
              >
                {children}
              </button>
            )
          }

          const x = { render: item }

          return <x.render key={i} />
        })}
        {onClose && (
          <button
            className={classNames(style.link, style.arrow)}
            onClick={onClose}
          >
            <Icon
              size="medium"
              type="arrow-left"
              className={classNames(style['action-icon'])}
            />
          </button>
        )}
        {onExit && (
          <button
            className={classNames(style.link, style.cross)}
            aria-label="Close Navigation"
            data-test-id="main-navdrawer-close"
            onClick={onExit}
          >
            <Icon
              size="small"
              type="cross"
              className={classNames(style['action-icon'])}
            />
          </button>
        )}
      </div>
      {(() => {
        if (path?.length) {
          const i = path[0]
          const item = items[i]
          if (item && 'items' in item) {
            return (
              <Pane
                key={i}
                depth={depth + 1}
                title={item.hideTitle ? undefined : item.title}
                items={item.items}
                initialPath={path?.slice(1)}
                onClose={() => {
                  setPath([])
                  trackEvent(
                    analyticsUtils.categories.UNIVERSAL_HEADER,
                    analyticsUtils.actions.UNIVERSAL_HEADER_NAV_MENU_CLOSE,
                    `L${depth}-${i} ${item.title}`
                  )
                }}
                onExit={onExit}
              />
            )
          }
        }

        return null
      })()}
    </>
  )
}

export default Pane
